import ServiceBase from './serviceBase';
import {
  OperationFilter,
  OperationList,
  Operation,
  DocumentFilter,
  MissionFilter,
  OperationMission,
  OperationDocument,
  OperationBasicItem,
  MiciCaseFilter,
  OperationMiciCase,
  OperationGrievance,
} from './types/Operation';
import {
  TaskFilter,
  OperationTaskList,
  CreateOperationTask,
  OperationTask,
  DelegateOperationTask,
  DeleteOperationTask,
} from './types/OperationTask';
import {
  Toolkit,
  Answer,
  ToolkitRequest,
  FinishToolkitState,
  DownloadToolkitRequest,
  ToolkitCalculatedValues,
} from './types/Toolkit';
import { Paged, Year } from './types/Misc';
import { YearQuery } from '../containers/Operations/Operation/YearQueryReducer';
import { GrievanceFilter } from './types/Grievance';

class OperationService extends ServiceBase {
  exportExcel = async (filter: OperationFilter): Promise<boolean> => {
    await this.download('operations/excel', filter);
    return true;
  };

  getOperations = async (filter: OperationFilter): Promise<OperationList> =>
    this.post<OperationList>('operations/search', filter);

  getOperationList = async (filter: { query: string }): Promise<OperationBasicItem[]> => {
    if (!filter.query || !filter.query.trim()) {
      return [];
    }
    return this.get<OperationBasicItem[]>(`operations/searchbynumber/${filter.query}`);
  };

  getById = async (id: string): Promise<Operation> => this.get<Operation>(`operations/${id}`);

  getByNumber = async (number: string): Promise<Operation> =>
    this.get<Operation>(`operations/byNumber/${number}`);

  getDocuments = async (filter: DocumentFilter): Promise<Paged<OperationDocument>> =>
    this.post<Paged<OperationDocument>>('operations/documents', filter);

  getMissions = async (filter: MissionFilter): Promise<Paged<OperationMission>> =>
    this.post<Paged<OperationMission>>('operations/missions', filter);

  getMiciCases = async (filter: MiciCaseFilter): Promise<Paged<OperationMiciCase>> =>
    this.post<Paged<OperationMiciCase>>('operations/miciCases', filter);

  getGrievances = async (filter: GrievanceFilter): Promise<Paged<OperationGrievance>> =>
    this.post<Paged<OperationGrievance>>('operations/grievances', filter);

  uploadDocument = async (formData: FormData): Promise<boolean> => {
    const result = await this.post<boolean>('operationdocuments', formData);
    return result;
  };

  downloadDocument = async (docId: number): Promise<boolean> => {
    await this.download(`operationdocuments/${docId}`);
    return true;
  };

  deleteDocument = async (docId: number): Promise<boolean> => {
    await this.delete(`operationdocuments/${docId}`);
    return true;
  };

  postToolkitAnswers = async (req: ToolkitRequest, type: string): Promise<void> =>
    this.post<void>(`operationToolkits/${req.operationId}/toolkit/${type}`, req.answers);

  getToolkitAnswers = async (req: ToolkitRequest): Promise<Answer[]> => {
    const toolkitIdUrl = req.toolkitId ? `/${req.toolkitId}` : '';
    return this.get<Answer[]>(`operationToolkits/${req.operationId}/toolkit${toolkitIdUrl}`);
  };

  getToolkits = async (operationId: string): Promise<Toolkit[]> =>
    this.get<Toolkit[]>(`operationToolkits/${operationId}/toolkits`);

  createToolkitDraftFromVersion = async (id: number): Promise<void> =>
    this.get<void>(`operationToolkits/toolkit/createFrom/${id}`);

  getCalculatedValues = async (
    req: ToolkitRequest,
    type: string
  ): Promise<ToolkitCalculatedValues> =>
    this.post<ToolkitCalculatedValues>(
      `operationToolkits/${req.operationId}/calculatedValues/${type}`,
      req.answers
    );

  finishToolkit = async (req: FinishToolkitState, type: string): Promise<void> =>
    this.post<void>(`operationToolkits/${req.operationId}/finishToolkit/${type}`, req);

  downloadToolkitDocument = async (data: DownloadToolkitRequest): Promise<boolean> => {
    await this.download(`operationToolkits/toolkit/${data.toolkitId}/download`, data);
    return true;
  };

  deleteToolkit = async (toolkitId: number): Promise<void> =>
    this.delete(`operationToolkits/toolkit/${toolkitId}`);

  getTasks = async (filter: TaskFilter): Promise<OperationTaskList> => {
    const { operationId, pageNumber, sortDescending, sortField } = filter;
    const queryParams = `pageNumber=${pageNumber}&pageSize=10&sortField=${sortField}&sortDescending=${sortDescending}`;
    return this.get<OperationTaskList>(`operations/${operationId}/tasks?${queryParams}`);
  };

  createTask = async (payload: CreateOperationTask): Promise<OperationTask> =>
    this.post<OperationTask>(`operations/${payload.operationId}/tasks`, payload);

  updateTask = async (payload: CreateOperationTask): Promise<OperationTask> =>
    this.put<OperationTask>(`operations/${payload.operationId}/tasks/${payload.taskId}`, payload);

  delegateTask = async (payload: DelegateOperationTask): Promise<OperationTask> =>
    this.post<OperationTask>(
      `operations/${payload.operationId}/tasks/${payload.taskId}/delegate`,
      payload
    );

  removeTaskDelegation = async (id: number): Promise<void> =>
    this.delete(`operationtaskowner/delegate/${id}`);

  deleteTask = async (payload: DeleteOperationTask): Promise<OperationTask> =>
    this.delete<OperationTask>(`operations/${payload.operationId}/tasks/${payload.taskId}`);

  getYearsSearch = async (filter: YearQuery): Promise<Year[]> => {
    if (!filter.query || !filter.query.trim()) {
      return [];
    }
    return this.get<Year[]>(`operations/searchYears/${filter.query}`);
  };
}

const operationService = new OperationService();

export default operationService;
